import React, { useEffect, useState } from "react";
import { CurrencyEur } from "@phosphor-icons/react";
import { api } from "../lib/api";

const LABELS = {
  phone_click: "Clics téléphone",
  whatsapp_click: "Clics WhatsApp",
  contact_request: "Demandes reçues",
  view: "Vues de la fiche",
};

/**
 * Financial impact estimator card. Fetches /dashboard/businesses/{slug}/impact,
 * shows estimated euro value over a configurable window, breakdown by type,
 * trend vs previous period, and an upsell CTA for non-Premium.
 */
export default function ImpactPanel({ slug, tier }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/dashboard/businesses/${slug}/impact`, { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [slug, days]);

  if (!data) return null;
  const total = data.total_revenue_eur || 0;
  const trend = data.trend_pct;
  const isEmpty = total === 0;
  const isPremium = tier !== "free";
  const trendColor = trend == null ? "text-muted-foreground" : trend >= 0 ? "text-primary" : "text-secondary";
  const trendArrow = trend == null ? "·" : trend >= 0 ? "▲" : "▼";

  return (
    <div className="rounded-sm overflow-hidden mb-6 border border-border bg-gradient-to-br from-card to-accent/20" data-testid="impact-panel">
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-1">
              <CurrencyEur size={14} weight="fill"/> Impact financier estimé
            </div>
            <div className="text-xs text-muted-foreground">Les {days} derniers jours · Base : {data.ticket_label} ({data.ticket_eur} €)</div>
          </div>
          <div className="flex items-center gap-1 border border-border rounded-sm overflow-hidden text-[11px]">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-2.5 py-1 font-semibold ${days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`impact-range-${d}`}>
                {d}j
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div>
            <div className="font-display text-4xl sm:text-5xl font-medium tracking-tight tabular-nums" data-testid="impact-total">
              {isEmpty ? "—" : total.toLocaleString("fr-FR")}
              {!isEmpty && <span className="text-2xl text-muted-foreground ml-1">€</span>}
            </div>
            {trend != null && (
              <div className={`text-xs font-semibold mt-1 ${trendColor}`} data-testid="impact-trend">
                {trendArrow} {Math.abs(trend)}% vs période précédente
              </div>
            )}
            {isEmpty && (
              <div className="text-xs text-muted-foreground mt-2 max-w-[240px]">Pas encore d'interactions mesurées. Votre fiche va bientôt être vue — les données arrivent sous 48 h.</div>
            )}
          </div>

          <div className="space-y-1.5">
            {data.breakdown?.filter((b) => b.count > 0).slice(0, 4).map((row) => (
              <div key={row.type} className="flex items-center gap-3 text-xs" data-testid={`impact-row-${row.type}`}>
                <div className="flex-1 text-muted-foreground truncate">{LABELS[row.type] || row.type}</div>
                <div className="font-mono tabular-nums text-foreground/80">{row.count}</div>
                <div className="text-muted-foreground">×</div>
                <div className="font-mono tabular-nums text-muted-foreground">{Math.round(row.conversion_rate * 100)}%</div>
                <div className="w-16 text-right font-semibold tabular-nums">{row.revenue_eur.toLocaleString("fr-FR")} €</div>
              </div>
            ))}
            {data.breakdown?.every((b) => b.count === 0) && (
              <div className="text-xs text-muted-foreground italic">Aucune interaction sur la période.</div>
            )}
          </div>
        </div>

        {!isPremium && total > 0 && (
          <div className="mt-5 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4" data-testid="impact-upsell">
            <div className="text-sm text-foreground/85 leading-relaxed flex-1 min-w-[240px]">
              Avec Premium, vos clics tél/WhatsApp deviennent <b>cliquables directement</b> et votre fiche remonte en tête — typiquement <b>+120 à +180%</b> d'impact.
            </div>
            <a href="/passez-partenaire" className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm text-sm font-semibold whitespace-nowrap" data-testid="impact-upgrade-cta">
              Passer Premium →
            </a>
          </div>
        )}

        <div className="mt-4 text-[10px] text-muted-foreground/70 leading-relaxed">
          {data.note}
        </div>
      </div>
    </div>
  );
}
